import React, { useEffect, useRef, useCallback } from "react";
import * as vegaEmbed from "vega-embed";

const PALETTE_SMALL = ["#3E4784", "#E31B54", "#EBC405", "#0086C9", "#7763CF"];
const PALETTE_MEDIUM = [
  "#3E4784", "#E31B54", "#EBC405", "#0086C9", "#7763CF",
  "#FF6B35", "#44BBA4", "#E94F37", "#393E41", "#B8B8B8",
];

interface ChartRendererProps {
  spec: Record<string, unknown>;
  data: Record<string, unknown>[];
  onChartLoaded?: (chartType: string) => void;
}

export const ChartRenderer: React.FC<ChartRendererProps> = ({
  spec,
  data,
  onChartLoaded,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const embedRef = useRef<vegaEmbed.EmbedResult | null>(null);

  const getColorPalette = useCallback(() => {
    if (!data.length) return PALETTE_SMALL;
    const colorField = (spec as any)?.encoding?.color?.field;
    if (!colorField) return PALETTE_SMALL;
    const unique = new Set(data.map((row) => row[colorField]));
    if (unique.size <= 5) return PALETTE_SMALL;
    if (unique.size <= 10) return PALETTE_MEDIUM;
    return undefined;
  }, [spec, data]);

  useEffect(() => {
    if (!containerRef.current || !spec || Object.keys(spec).length === 0) return;

    if (embedRef.current) {
      embedRef.current.finalize();
      embedRef.current = null;
    }

    const palette = getColorPalette();
    const specWithData: Record<string, unknown> = {
      ...spec,
      $schema: "https://vega.github.io/schema/vega-lite/v5.json",
      data: { values: data },
      width: "container",
      height: 320,
    };

    if (palette && (specWithData as any)?.encoding?.color) {
      (specWithData as any).encoding.color = {
        ...(specWithData as any).encoding.color,
        scale: { range: palette },
      };
    }

    vegaEmbed.default(containerRef.current, specWithData as any, {
      mode: "vega-lite",
      renderer: "svg",
      actions: {
        export: true,
        source: false,
        editor: false,
      },
    })
      .then((result) => {
        embedRef.current = result;
        const chartType = (spec as any)?.mark?.type || (spec as any)?.mark || "unknown";
        onChartLoaded?.(chartType);
      })
      .catch((err) => console.error("Vega-Lite render error:", err));

    return () => {
      if (embedRef.current) {
        embedRef.current.finalize();
        embedRef.current = null;
      }
    };
  }, [spec, data, getColorPalette, onChartLoaded]);

  return (
    <div
      ref={containerRef}
      className="w-full rounded-lg border border-gray-200 bg-white p-4"
      style={{ minHeight: "320px" }}
    />
  );
};
